import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useClinicMedicalSpecialty } from '../../hooks/useClinicMedicalSpecialty';
import { useAgendaCustomAppointmentTypes } from '../../hooks/useAgendaCustomAppointmentTypes';
import { buildAgendaAppointmentTypes } from '../../utils/buildAgendaAppointmentTypes';
import { isCommitmentAppointmentType } from '../../constants/appointmentTypes';
import { getPatientPanelStatusConfig } from '../patientPanelTheme';

type Props = {
  patientId: string;
  formatDate: (d: string) => string;
  onOpenAppointment: (apt: { id?: string }) => void;
  openingAppointmentId?: string | null;
  /** Increment to refetch after remarcação em lote ou edição externa. */
  reloadKey?: number;
  /** Quando false, não busca dados (componente montado só após expandir). */
  enabled?: boolean;
};

function formatTimeRange(start?: string | null, end?: string | null): string {
  const s = start ? String(start).substring(0, 5) : '';
  const e = end ? String(end).substring(0, 5) : '';
  if (s && e && s !== e) return `${s} às ${e}`;
  if (s) return s;
  return '—';
}

function appointmentWhenMs(apt: any): number {
  const d = String(apt?.appointment_date || '').trim();
  const t = String(apt?.start_time || '00:00:00').trim();
  const iso = d ? `${d}T${t.length <= 5 ? `${t}:00` : t}` : '';
  const ms = iso ? Date.parse(iso) : NaN;
  if (Number.isFinite(ms)) return ms;
  const created = Date.parse(String(apt?.created_at || ''));
  return Number.isFinite(created) ? created : 0;
}

/** Mais recente primeiro (data/hora decrescente). */
function sortAppointmentsNewestFirst(rows: any[]): any[] {
  return [...rows].sort((a, b) => appointmentWhenMs(b) - appointmentWhenMs(a));
}

function collectProcedureIds(rows: any[]): string[] {
  const ids = new Set<string>();
  for (const row of rows) {
    for (const id of Array.isArray(row?.procedure_ids) ? row.procedure_ids : []) {
      if (id) ids.add(String(id));
    }
    if (row?.billing_procedure_id) ids.add(String(row.billing_procedure_id));
  }
  return Array.from(ids);
}

const headCellSx = {
  fontWeight: 700,
  fontSize: '0.72rem',
  color: '#64748b',
  py: 1,
  borderBottom: '1px solid #e2e8f0',
  whiteSpace: 'nowrap' as const,
};

const bodyCellSx = {
  fontSize: '0.78rem',
  color: '#0f172a',
  py: 1.1,
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'top' as const,
};

const PatientAppointmentHistoryTable: React.FC<Props> = ({
  patientId,
  formatDate,
  onOpenAppointment,
  openingAppointmentId,
  reloadKey = 0,
  enabled = true,
}) => {
  const { specialty } = useClinicMedicalSpecialty();
  const { customTypes, builtinOverrides } = useAgendaCustomAppointmentTypes();
  const typeCatalog = useMemo(
    () => buildAgendaAppointmentTypes(specialty, customTypes, builtinOverrides),
    [specialty, customTypes, builtinOverrides],
  );

  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [procedureNameById, setProcedureNameById] = useState<Record<string, string>>({});

  const fetchPage = useCallback(async () => {
    if (!patientId || !enabled) return;
    setLoading(true);
    try {
      const { data } = await axios.get(getApiUrl('/clinic/appointments'), {
        params: {
          patient_id: patientId,
          page: page + 1,
          limit: rowsPerPage,
          sort_dir: 'desc',
          order: 'desc',
        },
      });
      const list = data?.success ? (data.data || []) : [];
      const filtered = sortAppointmentsNewestFirst(
        list.filter(
          (a: any) => !isCommitmentAppointmentType(String(a?.appointment_type || '')),
        ),
      );
      setRows(filtered);
      setTotal(Number(data?.total) || filtered.length);

      const procIds = collectProcedureIds(filtered);
      if (procIds.length > 0) {
        const procRes = await axios.get(getApiUrl('/clinic/procedures'), {
          params: { ids: procIds.join(','), limit: procIds.length },
        });
        const procRows = Array.isArray(procRes.data?.data) ? procRes.data.data : [];
        const patch: Record<string, string> = {};
        for (const p of procRows) {
          if (p?.id) patch[p.id] = String(p.name || p.code || '').trim() || p.id;
        }
        if (Object.keys(patch).length > 0) {
          setProcedureNameById((prev) => ({ ...prev, ...patch }));
        }
      }
    } catch (e) {
      console.error('Error loading appointment history:', e);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [patientId, page, rowsPerPage, reloadKey, enabled]);

  useEffect(() => {
    setPage(0);
  }, [patientId]);

  useEffect(() => {
    void fetchPage();
  }, [fetchPage]);

  const procedureLabelsFor = (apt: any) => {
    const ids = [
      ...(Array.isArray(apt.procedure_ids) ? apt.procedure_ids : []),
      apt.billing_procedure_id,
    ].filter(Boolean) as string[];
    const unique = Array.from(new Set(ids));
    const names = unique.map((id) => procedureNameById[id]).filter(Boolean);
    return names.length ? names.join(', ') : '—';
  };

  const typeChipFor = (apt: any) => {
    const key = String(apt?.appointment_type || '').trim();
    if (!key) return '—';
    const def = typeCatalog[key];
    const label = def?.label || apt.title || key;
    const color = def?.color || '#64748b';
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 600,
          bgcolor: `${color}22`,
          color,
          maxWidth: 180,
          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
        }}
      />
    );
  };

  return (
    <Box>
      <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f8fafc' }}>
              <TableCell sx={headCellSx}>Data</TableCell>
              <TableCell sx={headCellSx}>Horário</TableCell>
              <TableCell sx={headCellSx}>Status</TableCell>
              <TableCell sx={headCellSx}>Profissional</TableCell>
              <TableCell sx={headCellSx}>Procedimentos</TableCell>
              <TableCell sx={headCellSx}>Observação</TableCell>
              <TableCell sx={headCellSx}>Marcador / Categoria</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, color: '#64748b', fontSize: '0.82rem' }}>
                  Nenhum agendamento registrado.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((apt) => {
                const st = getPatientPanelStatusConfig(String(apt.status || 'scheduled'));
                const prof = apt.clinic_professionals?.full_name || '—';
                const notes = String(apt.notes || '').trim() || '—';
                const opening = openingAppointmentId === apt.id;
                return (
                    <TableRow
                      key={apt.id}
                      hover
                      title="Clique para editar a consulta"
                      onClick={() => { if (!opening) onOpenAppointment(apt); }}
                      sx={{
                        cursor: opening ? 'wait' : 'pointer',
                        opacity: opening ? 0.7 : 1,
                        '&:hover': { bgcolor: '#f8fafc' },
                      }}
                    >
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatDate(apt.appointment_date)}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, whiteSpace: 'nowrap', color: '#2563eb', fontWeight: 600 }}>
                        {formatTimeRange(apt.start_time, apt.end_time)}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          {opening ? (
                            <CircularProgress size={12} />
                          ) : (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: st.color,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Typography component="span" sx={{ fontSize: '0.78rem', color: st.color, fontWeight: 600 }}>
                            {st.label}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>{prof}</TableCell>
                      <TableCell sx={{ ...bodyCellSx, maxWidth: 200, wordBreak: 'break-word' }}>
                        {procedureLabelsFor(apt)}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, maxWidth: 220, wordBreak: 'break-word', color: '#64748b' }}>
                        {notes}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>{typeChipFor(apt)}</TableCell>
                    </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, pl: 0.5 }}>
          {total} {total === 1 ? 'Registro' : 'Registros'}
        </Typography>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          labelRowsPerPage="Linhas por página"
          labelDisplayedRows={({ from, to, count }) => `Página ${Math.floor(from / rowsPerPage) + 1} — ${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
          sx={{
            border: 'none',
            '.MuiTablePagination-toolbar': { minHeight: 40, pr: 0 },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem' },
          }}
        />
      </Box>
    </Box>
  );
};

export default PatientAppointmentHistoryTable;
